import jwt_decode from "jwt-decode";
import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import { loginInfo } from "../../../recoil/atoms";
import { Permissions } from "./permissions";

interface Token {
  data?: {
    role?: string;
    allowed_microservices: any;
  };
}
const useAuthorization = () => {
  const loggedInEmployee = useRecoilValue(loginInfo);

  const canGetToken = () => {
    const token = loggedInEmployee.FrontendToken!;
    if (token) return jwt_decode(token);
  };
  const decodedToken = canGetToken() as Token;

  const allowedServices = useMemo(() => {
    let services = {};
    Object.values(decodedToken.data?.allowed_microservices).forEach((module: any) => {
      services = Object.assign(services, module);
    });
    return services;
  }, [decodedToken]);

  const givePermission = (permission: string) => {
    return allowedServices[permission] ? true : false;
  };

  //auth
  const canGetAllRoles = givePermission(Permissions.GET_ROLES);
  const canGetUserBranches = givePermission(Permissions.GET_USER_BRANCHES);
  const canGetRole = givePermission(Permissions.GET_ROLE);
  const canCreateRole = givePermission(Permissions.CREATE_ROLE);
  const canDeleteRole = givePermission(Permissions.DELETE_ROLE);
  const canCreateRolePermission = givePermission(Permissions.CREATE_ROLE_PERMISSION);

  //clinic-management
  const canAddEmployeeUser = givePermission(Permissions.ADD_EMPLOYEE_USER);
  const canEditEmployeeUser = givePermission(Permissions.EDIT_EMPLOYEE_USER);
  const canAddEmployeeTenant = givePermission(Permissions.ADD_EMPLOYEE_TENANT);
  const canGetEmployeesTenant = givePermission(Permissions.GET_EMPLOYEES_TENANT);
  const canEditEmployeeTenant = givePermission(Permissions.EDIT_TENANT_EMPLOYEE);
  const canDeleteEmployeeTenant = givePermission(Permissions.DELETE_TENANT_EMPLOYEE);
  const canGetEmployeeTenantByID = givePermission(Permissions.GET_TENANT_EMPLOYEE_BY_ID);
  const canApproveEmployeeTenant = givePermission(Permissions.APPROVE_EMPLOYEE_TENANT);
  const canGetEmployeeSignups = givePermission(Permissions.GET_EMPLOYEE_SIGNUPS);
  const canSignUpEmployeeTenant = givePermission(Permissions.SIGN_UP_EMPLOYEE_TENANT);
  const canAddTenantBranch = givePermission(Permissions.ADD_BRANCH_TENANT);
  const canGetTenantBranches = givePermission(Permissions.GET_BRANCHES_TENANT);
  const canEditTenantBranch = givePermission(Permissions.EDIT_TENANT_BRANCH);
  const canDeleteTenantBranch = givePermission(Permissions.DELETE_TENANT_BRANCH);
  const canGetTenantBranchByID = givePermission(Permissions.GET_TENANT_BRANCH_BY_ID);
  const canRemoveEmployeeFromBranch = givePermission(Permissions.REMOVE_EMPLOYEE_FROM_BRANCH);
  const canGiveBranchAccess = givePermission(Permissions.GIVE_BRANCH_ACCESS);

  //medical-operations
  const canGetTenantPatients = givePermission(Permissions.GET_PATIENTS_OF_TENANT);
  const canGetTenantPatientByID = givePermission(Permissions.GET_TENANT_PATIENT_BY_ID);
  const canEditTenantPatient = givePermission(Permissions.EDIT_TENANT_PATIENT);
  const canAddPatientUser = givePermission(Permissions.ADD_PATIENT_USER);
  const canAddTenantPatient = givePermission(Permissions.ADD_PATIENT_TO_TENANT);
  const canDeleteTenantPatient = givePermission(Permissions.DELETE_TENANT_PATIENT);
  const canScheduleAppointment = givePermission(Permissions.SCHEDULE_APPOINTMENT);
  const canGetAppointments = givePermission(Permissions.GET_APPOINTMENTS);
  const canEditAppointment = givePermission(Permissions.EDIT_APPOINTMENT);
  const canDeleteAppointment = givePermission(Permissions.DELETE_APPOINTMENT);
  const canGetAppointmentByID = givePermission(Permissions.GET_APPOINTMENT_BY_ID);
  const canGetAppointmentTreeByID = givePermission(Permissions.GET_APPOINTMENT_TREE_BY_ID);
  const canAddDiagnosis = givePermission(Permissions.ADD_DIAGNOSIS);
  const canGetDiagnoses = givePermission(Permissions.GET_DIAGNOSES);
  const canEditDiagnosis = givePermission(Permissions.EDIT_DIAGNOSIS);
  const canDeleteDiagnosis = givePermission(Permissions.DELETE_DIAGNOSIS);
  const canGetDiagnosisByID = givePermission(Permissions.GET_DIAGNOSIS_BY_ID);
  const canAddPatientDiagnosis = givePermission(Permissions.ADD_PATIENT_DIAGNOSIS);
  const canGetPatientDiagnoses = givePermission(Permissions.GET_PATIENT_DIAGNOSES);
  const canEditPatientDiagnosis = givePermission(Permissions.EDIT_PATIENT_DIAGNOSIS);
  const canDeletePatientDiagnosis = givePermission(Permissions.DELETE_PATIENT_DIAGNOSIS);
  const canGetPatientDiagnosisByID = givePermission(Permissions.GET_PATIENT_DIAGNOSIS_BY_ID);
  const canEditAppointmentObservation = givePermission(Permissions.EDIT_APPOINTMENT_OBSERVATION);
  const canDeleteAppointmentObservation = givePermission(Permissions.DELETE_APPOINTMENT_OBSERVATION);
  const canAddAppointmentObservation = givePermission(Permissions.ADD_APPOINTMENT_OBSERVATION);
  const canEditPatientObservation = givePermission(Permissions.EDIT_PATIENT_OBSERVATION);
  const canDeletePatientObservation = givePermission(Permissions.DELETE_PATIENT_OBSERVATION);
  const canAddPatientObservation = givePermission(Permissions.ADD_PATIENT_OBSERVATION);
  const canAddMedication = givePermission(Permissions.ADD_MEDICATION);
  const canGetMedication = givePermission(Permissions.GET_MEDICATION);
  const canGetMedicationByID = givePermission(Permissions.GET_MEDICATION_BY_ID);
  const canEditMedication = givePermission(Permissions.EDIT_MEDICATION);
  const canDeleteMedication = givePermission(Permissions.DELETE_MEDICATION);
  const canAddTests = givePermission(Permissions.ADD_TESTS);
  const canGetTests = givePermission(Permissions.GET_TESTS);
  const canGetTestsByID = givePermission(Permissions.GET_TESTS_BY_ID);
  const canEditTests = givePermission(Permissions.EDIT_TESTS);
  const canDeleteTests = givePermission(Permissions.DELETE_TESTS);
  const canAddEquipment = givePermission(Permissions.ADD_EQUIPEMNT);
  const canGetEquipment = givePermission(Permissions.GET_EQUIPEMNT);
  const canGetEquipmentByID = givePermission(Permissions.GET_EQUIPEMNT_BY_ID);
  const canEditEquipment = givePermission(Permissions.EDIT_EQUIPEMNT);
  const canDeleteEquipment = givePermission(Permissions.DELETE_EQUIPEMNT);
  const canGetMedicationByCode = givePermission(Permissions.GET_MEDICATION_BY_CODE);
  const canGetEquipmentByCode = givePermission(Permissions.GET_EQUIPMENT_BY_CODE);
  const canGetTestByCode = givePermission(Permissions.GET_TEST_BY_CODE);
  const canAddDefaultSets = givePermission(Permissions.ADD_DEFAULT_SETS);
  const canGetDefaultSets = givePermission(Permissions.GET_DEFAULT_SETS);
  const canGetDefaultSetsByCode = givePermission(Permissions.GET_DEFAULT_SETS_BY_CODE);
  const canEditDefaultSets = givePermission(Permissions.EDIT_DEFAULT_SETS);
  const canDeleteDefaultSets = givePermission(Permissions.DELETE_DEFAULT_SETS);
  const canGetDefaultSetsById = givePermission(Permissions.GET_DEFAULT_SETS_BY_ID);
  const canGetFilteredCalendar = givePermission(Permissions.GET_FILTERED_CALENDAR);
  const canEditPatientTreatment = givePermission(Permissions.EDIT_PATIENT_TREATMENT);
  const canCreatePatientTreatment = givePermission(Permissions.CREATE_PATIENT_TREATMENT);
  const canGetPatientTreatmentById = givePermission(Permissions.GET_PATIENT_TREATMENT_BY_ID);
  const canDeletePatientTreatmentById = givePermission(Permissions.DELETE_PATIENT_TREATMENT_BY_ID);
  const canGetAllPatientTreatmentsOfDiagnosis = givePermission(Permissions.GET_ALL_PATIENT_TREATMENTS_OF_DIAGNOSIS);
  const canAddPatientTests = givePermission(Permissions.ADD_PATIENT_TESTS);
  const canGetPatientTests = givePermission(Permissions.GET_PATIENT_TESTS);
  const canEditPatientTests = givePermission(Permissions.EDIT_PATIENT_TESTS);
  const canDeletePatientTests = givePermission(Permissions.DELETE_PATIENT_TESTS);
  const canGetPatientTestsById = givePermission(Permissions.GET_PATIENT_TESTS_BY_ID);
  const canEditTenantPatientAndUser = givePermission(Permissions.EDIT_TENANT_PATIENT_USER);
  const canAddPatientArchive = givePermission(Permissions.ADD_PATIENT_ARCHIVE);
  const canEditArchives = givePermission(Permissions.EDIT_ARCHIVES);
  const canGetArchives = givePermission(Permissions.GET_ARCHIVES);
  const canDeleteArchives = givePermission(Permissions.DELETE_ARCHIVES);
  const canGetArchivesById = givePermission(Permissions.GET_ARCHIVE_BY_ID);
  const canGetGroups = givePermission(Permissions.GET_GROUPS);
  const canEditGroups = givePermission(Permissions.EDIT_GROUPS);
  const canAddGroups = givePermission(Permissions.ADD_GROUPS);
  const canDeleteGroups = givePermission(Permissions.DELETE_GROUPS);
  const canAddPatientToGroups = givePermission(Permissions.ADD_PATIENT_TO_GROUPS);
  const canGetGroupMembers = givePermission(Permissions.GET_GROUP_MEMBERS);
  const canDeletePatientFromGroup = givePermission(Permissions.DELETE_PATIENT_FROM_GROUP);
  const canCreateTemplate = givePermission(Permissions.CREATE_TEMPLATE);
  const canEditTemplate = givePermission(Permissions.EDIT_TEMPLATE);
  const canDeleteTemplate = givePermission(Permissions.DELETE_TEMPLATE);
  const canCreateInventory = givePermission(Permissions.CREATE_INVENTORY);
  const canEditCollections = givePermission(Permissions.EDIT_COLLECTIONS);
  const canAddCollections = givePermission(Permissions.ADD_COLLECTIONS);
  const canDeleteCollections = givePermission(Permissions.DELETE_COLLECTIONS);
  const canAddFields = givePermission(Permissions.ADD_FIELDS);
  const canDeleteField = givePermission(Permissions.DELETE_FIELD);

  const canEditInventory = givePermission(Permissions.EDIT_INVENTORY);
  const canDeactivateInventory = givePermission(Permissions.DEACTIVATE_INVENTORY);
  const canActivateInventory = givePermission(Permissions.ACTIVATE_INVENTORY);
  const canEditItem = givePermission(Permissions.EDIT_ITEM);
  const canAddItem = givePermission(Permissions.ADD_ITEM);
  const canDeleteItem = givePermission(Permissions.DELETE_ITEM);

  //file-management
  const canUploadFile = givePermission(Permissions.UPLOAD_FILE);
  const canGetFile = givePermission(Permissions.GET_FILE);
  const canDeleteFile = givePermission(Permissions.DELETE_FILE);
  const canMoveFromTempToPermanent = givePermission(Permissions.MOVE_FROM_TEMP_TO_PERMANENT);
  const canDeleteOldTempFiles = givePermission(Permissions.DELETE_OLD_TEMP_FILES);
  const canDownloadFile = givePermission(Permissions.DOWNLOAD_FILE);
  const canMoveMultipleTempFilesToPermanent = givePermission(Permissions.MOVE_MULTIPLE_TEMP_FILES_TO_PERMANENT);

  //user-management
  const canAddUser = givePermission(Permissions.ADD_USER);
  const canGetUsers = givePermission(Permissions.GET_USERS);
  const canEditUser = givePermission(Permissions.EDIT_USER);
  const canDeleteUser = givePermission(Permissions.DELETE_USER);
  const canGetUserById = givePermission(Permissions.GET_USER_BY_ID);
  const canGetUserProfileById = givePermission(Permissions.GET_USER_PROFILE_BY_ID);
  const canUploadPhoto = givePermission(Permissions.UPLOAD_PHOTO);
  const canAddTenant = givePermission(Permissions.ADD_TENANT);
  const canGetTenants = givePermission(Permissions.GET_TENANT);
  const canEditTenant = givePermission(Permissions.EDIT_TENANT);
  const canDeleteTenant = givePermission(Permissions.DELETE_TENANT);
  const canGetTenantById = givePermission(Permissions.GET_TENANT_BY_ID);
  const canUploadTenantPhoto = givePermission(Permissions.UPLOAD_TENANT_PHOTO);

  // Payment Microservice
  const canAddCurrency = givePermission(Permissions.ADD_CURRENCY);
  const canGetCurrency = givePermission(Permissions.GET_CURRENCY);
  const canEditCurrency = givePermission(Permissions.EDIT_CURRENCY);
  const canDeleteCurrency = givePermission(Permissions.DELETE_CURRENCY);

  const canAddPatientReceipts = givePermission(Permissions.ADD_PATIENT_RECEIPT);
  const canGetPatientReceipts = givePermission(Permissions.GET_PATIENT_RECEIPT);
  const canEditPatientReceipts = givePermission(Permissions.EDIT_PATIENT_RECEIPT);
  const canDeletePatientReceipts = givePermission(Permissions.DELETE_PATIENT_RECEIPT);

  const canAddPatientBills = givePermission(Permissions.ADD_PATIENT_BILL);
  const canGetPatientBills = givePermission(Permissions.GET_PATIENT_BILL);
  const canEditPatientBills = givePermission(Permissions.EDIT_PATIENT_BILL);
  const canDeletePatientBills = givePermission(Permissions.DELETE_PATIENT_BILL);

  const canAddDiscounts = givePermission(Permissions.ADD_DISCOUNT);
  const canGetDiscounts = givePermission(Permissions.GET_DISCOUNT);
  const canEditDiscounts = givePermission(Permissions.EDIT_DISCOUNT);
  const canDeleteDiscounts = givePermission(Permissions.DELETE_DISCOUNT);

  const canGetBalances = givePermission(Permissions.GET_BALANCES);
  const canGetPatientBalance = givePermission(Permissions.GET_PATIENT_BALANCE);


  return {
    canAddTenantBranch,
    canGetTenantBranches,
    canEditTenantBranch,
    canDeleteTenantBranch,
    canGetTenantBranchByID,
    canSignUpEmployeeTenant,
    canGetEmployeeSignups,
    canApproveEmployeeTenant,
    canUploadFile,
    canGetAllRoles,
    canGetUserBranches,
    canGetRole,
    canCreateRole,
    canDeleteRole,
    canCreateRolePermission,
    canAddEmployeeUser,
    canEditEmployeeUser,
    canAddEmployeeTenant,
    canGetEmployeesTenant,
    canEditEmployeeTenant,
    canDeleteEmployeeTenant,
    canGetEmployeeTenantByID,
    canGetTenantPatients,
    canGetTenantPatientByID,
    canEditTenantPatient,
    canAddPatientUser,
    canAddTenantPatient,
    canDeleteTenantPatient,
    canScheduleAppointment,
    canGetAppointments,
    canEditAppointment,
    canGetAppointmentByID,
    canDeleteAppointment,
    canGetAppointmentTreeByID,
    canAddDiagnosis,
    canGetDiagnoses,
    canEditDiagnosis,
    canDeleteDiagnosis,
    canGetDiagnosisByID,
    canGetPatientDiagnoses,
    canAddPatientDiagnosis,
    canEditPatientDiagnosis,
    canGetPatientDiagnosisByID,
    canDeletePatientDiagnosis,
    canEditAppointmentObservation,
    canDeleteAppointmentObservation,
    canAddAppointmentObservation,
    canEditPatientObservation,
    canDeletePatientObservation,
    canAddPatientObservation,
    canAddMedication,
    canGetMedication,
    canGetMedicationByID,
    canEditMedication,
    canDeleteMedication,
    canAddTests,
    canGetTestsByID,
    canGetTests,
    canEditTests,
    canDeleteTests,
    canAddEquipment,
    canGetEquipment,
    canEditEquipment,
    canGetEquipmentByID,
    canDeleteEquipment,
    canGetMedicationByCode,
    canGetEquipmentByCode,
    canAddDefaultSets,
    canGetTestByCode,
    canGetDefaultSets,
    canGetDefaultSetsByCode,
    canEditDefaultSets,
    canDeleteDefaultSets,
    canGetDefaultSetsById,
    canGetFilteredCalendar,
    canEditPatientTreatment,
    canCreatePatientTreatment,
    canGetPatientTreatmentById,
    canDeletePatientTreatmentById,
    canGetAllPatientTreatmentsOfDiagnosis,
    canAddPatientTests,
    canGetPatientTests,
    canEditPatientTests,
    canDeletePatientTests,
    canEditTenantPatientAndUser,
    canGetPatientTestsById,
    canAddPatientArchive,
    canGetArchives,
    canEditArchives,
    canDeleteArchives,
    canGetArchivesById,
    canGetFile,
    canDeleteFile,
    canMoveFromTempToPermanent,
    canDeleteOldTempFiles,
    canDownloadFile,
    canMoveMultipleTempFilesToPermanent,
    canAddUser,
    canGetUsers,
    canEditUser,
    canDeleteUser,
    canGetUserById,
    canGetUserProfileById,
    canUploadPhoto,
    canAddTenant,
    canGetTenants,
    canEditTenant,
    canDeleteTenant,
    canGetTenantById,
    canUploadTenantPhoto,
    canGetGroups,
    canEditGroups,
    canAddGroups,
    canDeleteGroups,
    canAddPatientToGroups,
    canGetGroupMembers,
    canDeletePatientFromGroup,
    canEditCollections,
    canAddCollections,
    canDeleteCollections,
    canAddFields,
    canDeleteField,
    canEditTemplate,
    canCreateTemplate,
    canDeleteTemplate,
    canCreateInventory,
    canEditInventory,
    canDeactivateInventory,
    canActivateInventory,
    canEditItem,
    canAddItem,
    canDeleteItem,
    canRemoveEmployeeFromBranch,
    canGiveBranchAccess,
    canAddCurrency,
    canGetCurrency,
    canEditCurrency,
    canDeleteCurrency,
    canAddPatientReceipts,
    canGetPatientReceipts,
    canEditPatientReceipts,
    canDeletePatientReceipts,
    canAddPatientBills,
    canGetPatientBills,
    canEditPatientBills,
    canDeletePatientBills,
    canAddDiscounts,
    canGetDiscounts,
    canEditDiscounts,
    canDeleteDiscounts,
    canGetBalances,
    canGetPatientBalance
  };
};

export default useAuthorization;
